import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';

import { confirm } from '../App/AppDialog';
import { showError } from '../App/AppAlert';
import { useAuthState } from '../AuthState';
import MarketDatesPanel from '../Billing/MarketDatesPanel';
import ReviewInvoicePanel from '../Billing/ReviewInvoicePanel';
import { VendorSelectionPanel } from '../Billing/VendorSelectionPanel';
import { Adjustment } from '../Billing/InvoiceDetailView';
import { canUser } from '../../support/shared/permissionHelpers';
import { Link as NavLink } from '../Navigation';
import {
  Box,
  FormStepper,
  Heading,
  Progress,
  StepperStep,
  Text,
  VStack,
} from '../../core-ui';

import NoAccessPage from './NoAccessPage';
import {
  SendVendorInvoice,
  SendVendorInvoiceVariables,
} from './__generated__/SendVendorInvoice';
import { safeInvokeAsync } from '../../core/support/safeInvokeAsync';

const SEND_VENDOR_INVOICE = gql`
  mutation SendVendorInvoice(
    $vendorId: String!
    $selectedDates: [String!]!
    $adjustment: AdjustmentInput
    $signupFeeOnly: Boolean!
    $includeSalesData: Boolean!
  ) {
    sendVendorInvoice(
      vendorId: $vendorId
      selectedDates: $selectedDates
      adjustment: $adjustment
      signupFeeOnly: $signupFeeOnly
      includeSalesData: $includeSalesData
    ) {
      invoice {
        id
      }
      sendSuccess
      sendError
    }
  }
`;

type SendResult = { success: true } | { success: false; error: Error };

type VendorSendResult = { vendorId: string; result: SendResult };

type SendingState =
  | { name: 'NOT_STARTED'; results?: undefined }
  | { name: 'IN_PROGRESS'; results: Array<VendorSendResult> }
  | { name: 'COMPLETE'; results: Array<VendorSendResult>; error: Error | null };

export default function SendInvoicesPage() {
  const { user } = useAuthState();

  const [selectedMarketDates, setSelectedMarketDates] = useState(
    new Set<string>(),
  );
  const [signupFeeOnly, setSignupFeeOnly] = useState(false);
  const [includeSalesData, setIncludeSalesData] = useState<boolean>(false);
  const [selectedVendors, setSelectedVendors] = useState(new Set<string>());
  // This is intentionally used without the setter
  const [adjustments] = useState(() => new Map<string, Adjustment>());

  const [sendInvoiceMutation] = useMutation<
    SendVendorInvoice,
    SendVendorInvoiceVariables
  >(SEND_VENDOR_INVOICE);

  const [sendingState, setSendingState] = useState<SendingState>({
    name: 'NOT_STARTED',
  });

  const sendInvoice = async (
    vendorId: string,
    selectedDates: Array<string>,
    adjustment: Adjustment | null,
    signupFeeOnly: boolean,
    includeSalesData: boolean,
  ): Promise<SendResult> => {
    const [result, error] = await safeInvokeAsync(() => {
      return sendInvoiceMutation({
        variables: {
          vendorId,
          selectedDates,
          adjustment,
          signupFeeOnly,
          includeSalesData,
        },
      });
    });
    if (error) {
      return { success: false, error };
    }
    const serverError = result.errors?.[0];
    if (serverError) {
      return { success: false, error: serverError };
    }
    const sendResult = result.data?.sendVendorInvoice;
    if (!sendResult) {
      return { success: false, error: new Error('Invoice not sent') };
    }
    return { success: true };
  };

  const sendInvoices = async () => {
    const selectedDates = Array.from(selectedMarketDates);
    for (const vendorId of selectedVendors) {
      const adjustment = adjustments.get(vendorId) ?? null;
      const result = await sendInvoice(
        vendorId,
        selectedDates,
        adjustment,
        signupFeeOnly,
        includeSalesData,
      );
      setSendingState((state) => {
        const results = state.results ?? [];
        return {
          name: 'IN_PROGRESS',
          results: [...results, { vendorId, result }],
        };
      });
      if (!result.success) {
        // eslint-disable-next-line no-console
        console.log(`Error sending invoice for vendor ${vendorId}`);
        // eslint-disable-next-line no-console
        console.error(result.error);
      }
    }
  };

  const pageTitle = t('Create and Send Invoices');

  if (!canUser(user, { billing: 'WRITE' })) {
    return <NoAccessPage title={pageTitle} />;
  }

  if (sendingState.name === 'IN_PROGRESS') {
    return (
      <VStack p={10} alignItems="stretch">
        {/* TODO: Use an overlay or modal here */}
        <Box style={{ marginTop: 10, marginBottom: 15 }}>
          <Text>{t('Sending Invoices...')}</Text>
          <Progress
            hasStripe
            value={(sendingState.results.length / selectedVendors.size) * 100}
            mt={2}
          />
        </Box>
      </VStack>
    );
  }

  if (sendingState.name === 'COMPLETE') {
    return (
      <VStack p={10} alignItems="stretch">
        <Heading as="h2" size="lg">
          {t('Sending Complete')}
        </Heading>
        <VStack align="flex-start">
          <Text>
            {t('Successfully Sent: {successful}', {
              successful: sendingState.results.filter(
                ({ result }) => result.success,
              ).length,
            })}
          </Text>
          <Text>
            {t('Failed: {failed}', {
              failed: sendingState.results.filter(
                ({ result }) => !result.success,
              ).length,
            })}
          </Text>
          <NavLink to={['/billing/vendors']}>
            {t('Go to Billing Summary')}
          </NavLink>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack px={10} pt={4} flex={1} minH={0} alignItems="stretch" spacing={0}>
      <FormStepper
        submitText={t('Send')}
        onSubmit={async () => {
          if (selectedVendors.size === 0) {
            showError(t('No vendors selected'));
            return;
          }
          const shouldProceed = await confirm(
            t('Send Invoices?'),
            t(`Press OK to send an invoice to each of the selected vendors.`),
          );
          if (!shouldProceed) {
            return;
          }
          setSendingState({ name: 'IN_PROGRESS', results: [] });
          const [, error] = await safeInvokeAsync(() => sendInvoices());
          setSendingState((state) => {
            const results = state.results ?? [];
            return { name: 'COMPLETE', results, error };
          });
        }}
      >
        <StepperStep title={t('Select Market Dates')}>
          <MarketDatesPanel
            selectedMarketDates={selectedMarketDates}
            onSelectionChange={(selection) => setSelectedMarketDates(selection)}
            signupFeeOnly={signupFeeOnly}
            onSignupFeeOnlyChange={(value) => setSignupFeeOnly(value)}
          />
        </StepperStep>
        <StepperStep title={t('Select Vendors')}>
          <VendorSelectionPanel
            selectedMarketDates={selectedMarketDates}
            setSelectedVendors={setSelectedVendors}
            selectedVendors={selectedVendors}
            signupFeeOnly={signupFeeOnly}
          />
        </StepperStep>
        <StepperStep title={t('Review and Send')}>
          <ReviewInvoicePanel
            selectedDates={signupFeeOnly ? new Set() : selectedMarketDates}
            selectedVendors={selectedVendors}
            adjustments={adjustments}
            signupFeeOnly={signupFeeOnly}
            includeSalesData={includeSalesData}
            setIncludeSalesData={setIncludeSalesData}
          />
        </StepperStep>
      </FormStepper>
    </VStack>
  );
}
